import React, { useState } from "react";
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useColoresGrafico } from "../utils/colores";
import { MESES, MESES_CORTO, bs, compacto, numero } from "../utils/formato";

// Sin animación de entrada: las barras aparecen de una vez (más estable y respeta a quien reduce el movimiento).
// Marcas: columnas finas (<= 24 px) con 4 px redondeados en el extremo del dato y cuadradas en la base, 2 px de aire entre columnas vecinas,
// rejilla horizontal de 1 px y ejes discretos. El texto va siempre en tinta de texto; el color solo lo llevan las marcas.
const margen = { top: 8, right: 8, bottom: 0, left: 0 };

function Leyenda({ items }) {
  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 0.5 }}>
      {items.map((i) => (
        <Box key={i.nombre} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: i.color }} />
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 600 }}>{i.nombre}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// Tooltip: título + una fila por serie (marca de color + etiqueta + valor) y filas extra opcionales.
function TooltipGrafico({ active, payload, titulo, extra }) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const fila = payload[0].payload;
  return (
    <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.25, boxShadow: theme.shadows[3], minWidth: 170 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.5 }}>{titulo(fila)}</Typography>
      {payload.map((p) => (
        <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.15 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: p.color || p.payload?.fill }} />
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", flex: 1 }}>{p.name}</Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{bs(p.value)}</Typography>
        </Box>
      ))}
      {extra?.(fila)}
    </Box>
  );
}

function VistaTabla({ columnas, filas }) {
  return (
    <TableContainer sx={{ maxHeight: 320 }}>
      <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.4, px: 1, fontSize: 12.5 } }}>
        <TableHead>
          <TableRow>
            {columnas.map((c, i) => <TableCell key={c} align={i ? "right" : "left"} sx={{ fontWeight: 800 }}>{c}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((f) => (
            <TableRow key={f[0]}>
              {f.map((v, i) => <TableCell key={i} align={i ? "right" : "left"} sx={i ? { fontVariantNumeric: "tabular-nums" } : undefined}>{v}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const ejeX = (c) => ({ tick: { fill: c.tinta, fontSize: 12 }, tickLine: false, axisLine: { stroke: c.eje } });
const ejeY = (c) => ({ tick: { fill: c.tinta, fontSize: 12 }, tickLine: false, axisLine: false, tickFormatter: compacto, width: 44 });

// Facturado vs. cobrado de los 12 meses del año. Un clic en un mes lo elige para los indicadores y los demás bloques.
export function GraficoMensual({ mensual, mes, year, onElegirMes }) {
  const c = useColoresGrafico();
  const [verTabla, setVerTabla] = useState(false);
  const datos = mensual.map((m) => ({ ...m, nombre: MESES_CORTO[m.mes - 1] }));
  const serie = [{ nombre: "Facturado", color: c.facturado }, { nombre: "Cobrado", color: c.cobrado }];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Leyenda items={serie} />
        <Button size="small" onClick={() => setVerTabla((v) => !v)} sx={{ textTransform: "none", fontSize: 12 }}>{verTabla ? "Ver gráfico" : "Ver como tabla"}</Button>
      </Box>
      {verTabla ? (
        <VistaTabla
          columnas={[`Mes de ${year}`, "Facturado (Bs)", "Cobrado (Bs)"]}
          filas={mensual.map((m) => [MESES[m.mes - 1], numero(m.facturado), numero(m.cobrado)])}
        />
      ) : (
        <Box sx={{ height: 290 }} role="img" aria-label={`Facturado y cobrado por mes en ${year}`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={datos} margin={margen} barGap={2} barCategoryGap="22%" onClick={(e) => e?.activeLabel && onElegirMes(datos.find((d) => d.nombre === e.activeLabel)?.mes)}>
              <CartesianGrid vertical={false} stroke={c.rejilla} />
              <XAxis dataKey="nombre" {...ejeX(c)} />
              <YAxis {...ejeY(c)} />
              <Tooltip cursor={{ fill: alpha(c.tinta, 0.12) }} content={<TooltipGrafico titulo={(f) => `${MESES[f.mes - 1]} ${year}`} extra={(f) => <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>{numero(f.facturas)} facturas · {numero(f.pagos)} pagos</Typography>} />} />
              <Bar isAnimationActive={false} dataKey="facturado" name="Facturado" fill={c.facturado} barSize={12} maxBarSize={24} radius={[4, 4, 0, 0]} cursor="pointer">
                {datos.map((d) => <Cell key={d.mes} fill={c.facturado} fillOpacity={d.mes === mes ? 1 : 0.7} />)}
              </Bar>
              <Bar isAnimationActive={false} dataKey="cobrado" name="Cobrado" fill={c.cobrado} barSize={12} maxBarSize={24} radius={[4, 4, 0, 0]} cursor="pointer">
                {datos.map((d) => <Cell key={d.mes} fill={c.cobrado} fillOpacity={d.mes === mes ? 1 : 0.7} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>Toca un mes para ver su detalle arriba y en los demás bloques.</Typography>
    </Box>
  );
}

// Cartera por cobrar según antigüedad: una sola serie, rampa de un tono, valor al extremo de cada barra.
export function GraficoCartera({ cartera }) {
  const c = useColoresGrafico();
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ height: 250 }} role="img" aria-label="Cartera por cobrar según antigüedad de la factura">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={cartera} layout="vertical" margin={{ top: 4, right: 56, bottom: 0, left: 0 }} barCategoryGap="28%">
            <XAxis type="number" hide domain={[0, "dataMax"]} />
            <YAxis type="category" dataKey="etiqueta" width={124} tick={{ fill: theme.palette.text.secondary, fontSize: 12.5 }} tickLine={false} axisLine={{ stroke: c.eje }} />
            <Tooltip cursor={{ fill: alpha(c.tinta, 0.12) }} content={<TooltipGrafico titulo={(f) => f.etiqueta} extra={(f) => <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>{numero(f.facturas)} facturas</Typography>} />} />
            <Bar isAnimationActive={false} dataKey="monto" name="Por cobrar" barSize={18} maxBarSize={24} radius={[0, 4, 4, 0]}>
              {cartera.map((r, i) => <Cell key={r.clave} fill={c.rampa[i]} />)}
              <LabelList dataKey="monto" position="right" formatter={compacto} fill={theme.palette.text.secondary} fontSize={12} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Antigüedad desde la fecha de la factura, a hoy.</Typography>
    </Box>
  );
}

// Facturado vs. cobrado del mes elegido por sucursal.
export function GraficoSucursal({ datos, tituloMes }) {
  const c = useColoresGrafico();
  const serie = [{ nombre: "Facturado", color: c.facturado }, { nombre: "Cobrado", color: c.cobrado }];
  return (
    <Box>
      <Leyenda items={serie} />
      <Box sx={{ height: 250 }} role="img" aria-label={`Facturado y cobrado por sucursal en ${tituloMes}`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={datos} margin={margen} barGap={2} barCategoryGap="30%">
            <CartesianGrid vertical={false} stroke={c.rejilla} />
            <XAxis dataKey="sucursal" {...ejeX(c)} />
            <YAxis {...ejeY(c)} />
            <Tooltip cursor={{ fill: alpha(c.tinta, 0.12) }} content={<TooltipGrafico titulo={(f) => f.sucursal} extra={(f) => <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>Por cobrar hoy: {bs(f.por_cobrar)}</Typography>} />} />
            <Bar isAnimationActive={false} dataKey="facturado" name="Facturado" fill={c.facturado} barSize={16} maxBarSize={24} radius={[4, 4, 0, 0]} />
            <Bar isAnimationActive={false} dataKey="cobrado" name="Cobrado" fill={c.cobrado} barSize={16} maxBarSize={24} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
